import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import streamClient from '../../streamClient';
import { toast } from 'react-toastify';
import TopBar from '../TopBar/TopBar';
import MeetingKnowledgeSupport from './MeetingKnowledgeSupport';
import './MeetingEditor.css';

function MeetingEditor({ meetingId, rawTranscription }) {
  // Meeting data state
  const [streamingText, setStreamingText] = useState('');
  const [finalText, setFinalText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('document');
  const [isKnowledgeSupportOpen, setIsKnowledgeSupportOpen] = useState(false);

  // Tab configuration
  const tabs = [
    { id: 'document', label: 'Dokument' },
    { id: 'transcription', label: 'Transkribering' }
  ];

  useEffect(() => {
    if (!meetingId) return;
    fetchMeeting(meetingId);
  }, [meetingId]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchMeeting = async (id) => {
    try {
      const resp = await apiClient.get(`/meetings/${id}`);
      const data = resp.data;
      if (data.generated_meeting_text) {
        setFinalText(data.generated_meeting_text);
      }
      if (data.template_id) {
        setSelectedTemplateId(data.template_id);
      }
    } catch (err) {
      console.error('Kunde inte hämta möte:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const resp = await apiClient.get('/api/meetingmallar');
      setTemplates(resp.data);
    } catch (err) {
      console.error('Kunde inte hämta mallar:', err);
    }
  };

  const handleTemplateChange = async (templateId) => {
    const newId = parseInt(templateId, 10);
    setSelectedTemplateId(newId);
    await patchMeetingTemplate(newId);
  };

  const patchMeetingTemplate = async (templateId) => {
    if (!meetingId) return;
    try {
      await apiClient.patch(`/meetings/${meetingId}`, {
        template_id: templateId
      });
    } catch (err) {
      setError('Kunde inte uppdatera template_id.');
    }
  };

  const generateMeeting = async () => {
    if (!meetingId) return setError('meetingId saknas.');
    if (!selectedTemplateId) return setError('Välj en mötesmall först.');

    setError('');
    setStreamingText('');
    setFinalText('');
    setIsGenerating(true);

    let fullText = '';
    try {
      await streamClient.post(
        '/api/meeting/generate',
        { meeting_id: meetingId },
        (chunk) => {
          fullText += chunk;
          setStreamingText((prev) => prev + chunk);
        }
      );
      setFinalText(fullText);
      await saveMeetingText(fullText);
    } catch (err) {
      setError('Fel vid GPT-generering.');
    } finally {
      setIsGenerating(false);
    }
  };

  const saveMeetingText = async (text) => {
    if (!meetingId) return;
    try {
      await apiClient.patch(`/meetings/${meetingId}/update_meeting_text`, {
        generated_meeting_text: text,
      });
    } catch (err) {
      console.error('Kunde inte spara text:', err);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(finalText || '')
      .then(() => {
        toast.success('Texten är kopierad till urklipp!');
      })
      .catch(() => toast.error('Kunde inte kopiera texten.'));
  };

  const handleKnowledgeSupport = () => {
    setIsKnowledgeSupportOpen(true);
  };

  const handleTextUpdate = (newText) => {
    setFinalText(newText);
    saveMeetingText(newText);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'document':
        return (
          <div className="meeting-document-tab">
            <textarea
              className="meeting-main-textarea"
              value={finalText || streamingText}
              onChange={(e) => setFinalText(e.target.value)}
              onBlur={(e) => saveMeetingText(e.target.value)}
              placeholder="GPT-genererade anteckningar visas här..."
            />
            {error && <p className="error-message">{error}</p>}
          </div>
        );
      case 'transcription':
        return (
          <div className="meeting-transcription-tab">
            <div className="transcription-display">
              <p>{rawTranscription || 'Ingen transkribering tillgänglig'}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="meeting-editor-container">
      <TopBar
        showTabs={true}
        tabs={tabs.map(tab => tab.id)}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showTemplateDropdown={false}
      />

      <div className="meeting-editor-content">
        {renderTabContent()}
      </div>

      <div className="bottom-bar">
        <select
          className="template-bottom-select"
          value={selectedTemplateId || ''}
          onChange={(e) => handleTemplateChange(e.target.value)}
        >
          <option value="" disabled>-- Välj mötesmall --</option>
          {templates.map((tmpl) => (
            <option key={tmpl.meeting_template_id} value={tmpl.meeting_template_id}>
              {tmpl.template_name}
            </option>
          ))}
        </select>

        <button
          className="bottom-bar-button generate-button"
          onClick={generateMeeting}
          disabled={isGenerating}
        >
          {isGenerating ? 'Genererar...' : 'Generera om'}
        </button>

        <button
          className="bottom-bar-button copy-button"
          onClick={handleCopy}
          disabled={!finalText}
        >
          Kopiera
        </button>

        <button
          className="bottom-bar-button knowledge-button"
          onClick={handleKnowledgeSupport}
        >
          Kunskapsstöd
        </button>
      </div>

      <MeetingKnowledgeSupport
        isOpen={isKnowledgeSupportOpen}
        onClose={() => setIsKnowledgeSupportOpen(false)}
        meetingText={finalText}
        transcription={rawTranscription}
        onUpdateMeetingText={handleTextUpdate}
      />
    </div>
  );
}

export default MeetingEditor;
